/**
 * client/src/components/layout/removal-hold-push-listener.tsx
 *
 * Mounts the owner-side removal-hold push listener once, inside the
 * authenticated app shell, so a device with the app open learns that a
 * 48-hour removal hold opened (or ended) on this account whatever page it is
 * on — not only if it happens to be looking at Security Keys.
 *
 * Mirrors `recovery-push-listener.tsx` exactly (Reuse-First Check,
 * DEVELOPMENT/quality.md Code Review Criteria #8): on arrival, invalidate the
 * shared removal-holds query so `RemovalHoldNotice` re-derives from live
 * facts, and raise a neutral toast carrying the server's own frame message,
 * with no click-through link (same DEVIATION as the recovery listener: the
 * shared `toast()` primitive gained an optional link slot in #3169, but no
 * caller here uses it), so the reachable action stays the Security Keys
 * page's own hold card and its cancel button.
 *
 * NEVER red or amber: a hold is an accepted, waiting action, not an error
 * (CLAUDE.md §Design Constraints). The toast is the default neutral tone.
 *
 * Renders nothing. It is a listener, deliberately not a UI component.
 */

import { useQueryClient } from "@tanstack/react-query";

import { toast } from "@/components/ui/toast";
import { REMOVAL_HOLDS_QUERY_KEY } from "@/lib/api/removal-holds";
import { REMOVAL_HOLD_DUTIES_QUERY_KEY } from "@/lib/removal-hold-duties-api";
import {
  REMOVAL_HOLD_OPENED_EVENT,
  REMOVAL_HOLD_VETO_REQUEST_EVENT,
  useRemovalHoldPush,
} from "@/lib/use-removal-hold-push";

const OPENED_FALLBACK =
  "A removal was requested on your account. It will not take effect for 48 hours — review it in Security Keys.";
const DECIDED_FALLBACK = "A removal hold on your account has ended. See Security Keys for the outcome.";
// FALLBACK ONLY — used when a frame arrives with no message of its own. It
// deliberately promises NOTHING and names NO page (fix round 1, review findings
// A/D): whether this viewer can veto depends on the hold's conflict set (AC-7),
// the D-p kill switch and whether the ward's account is frozen, and which
// surface they have depends on whether their account is a contained
// guardian-only shell. The server's own message knows all four; this string
// knows none of them, so it may only point at the duty list that carries the
// honest per-row answer.
const VETO_REQUEST_FALLBACK =
  "Someone asked to remove a security key, guardian or bank account from an account you protect. Check what's waiting on you — it says whether you can stop this one.";

export function RemovalHoldPushListener(): null {
  const queryClient = useQueryClient();
  useRemovalHoldPush((frame) => {
    // #3009 — the guardian-addressed frame and the owner-addressed ones invalidate
    // DIFFERENT feeds. Invalidating the owner's holds query on a veto request
    // would refresh a list this viewer is not on and leave the duty rail stale,
    // which is the shape of bug that made the veto unreachable in the first place.
    const isVetoRequest = frame.type === REMOVAL_HOLD_VETO_REQUEST_EVENT;
    void queryClient.invalidateQueries({
      queryKey: isVetoRequest ? REMOVAL_HOLD_DUTIES_QUERY_KEY : REMOVAL_HOLDS_QUERY_KEY,
    });
    const fallback = isVetoRequest
      ? VETO_REQUEST_FALLBACK
      : frame.type === REMOVAL_HOLD_OPENED_EVENT
        ? OPENED_FALLBACK
        : DECIDED_FALLBACK;
    toast(typeof frame.message === "string" && frame.message.length > 0 ? frame.message : fallback);
  });
  return null;
}
